import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Divider,
    Button,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
} from "@mui/material";

import { useEntidade } from "../../../Hooks/EntidadeHook";
import { useGuiasStorage, ParcelaResumo } from "../../../Storage/guias";
import theme from "../../../Styles/theme";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import InputAdornment from "@mui/material/InputAdornment";
import { CarneDialog } from "./CarneDialog";

import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { formatDate, formatMoney, maskCpfCnpj } from "../../../Utils/Formatacoes";



type CarneAgrupado = {
    registro: string;
    rows: ParcelaResumo[];
    descr1?: string;
    descr2?: string;
    descr3?: string;
    total: number;
    parcelas: number;
    vencidas: number;
    proximoVencimento?: any;
    ultimoVencimento?: any;
};

const isVencida = (venc: any) => {
    if (!venc) return false;
    const d = new Date(venc);
    d.setDate(d.getDate() + 1); // considera vencida a guia do dia, após as 00:00:00
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d < hoje;
};

type FiltroVencimento = "TODOS" | "VENCIDOS" | "NAO_VENCIDOS";

export const HomeNaoAutenticada: React.FC = () => {
    const { banco } = useParams<{ banco: string }>();
    const isMobile = useMediaQuery("(max-width:900px)");

    const { getEntidadePorNomeDoBanco, entidade } = useEntidade();
    const id_entidade = entidade?.id;

    const { getGuiasPorCpfCnpj, guias, clear } = useGuiasStorage();

    const [cpfCnpj, setCpfCnpj] = useState<string>("");
    const [cpfCnpjDraft, setCpfCnpjDraft] = useState<string>("");
    const [pesquisado, setPesquisado] = useState<string>("");
    const [modalBuscaOpen, setModalBuscaOpen] = useState<boolean>(true);

    const [openCarneDialog, setOpenCarneDialog] = useState(false);
    const [cobrancaFocus, setCobrancaFocus] = useState([] as ParcelaResumo[]);

    // filtros
    const [termo, setTermo] = useState<string>("");
    const [filtroVencimento, setFiltroVencimento] = useState<FiltroVencimento>("TODOS");

    const normalize = (s: any) =>
        String(s ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")      // remove acentos
            .replace(/[^a-z0-9]+/g, " ")          // remove especiais (mantém letras/números)
            .replace(/\s+/g, " ")                 // compacta espaços
            .trim();

    const guiasFiltradas = useMemo(() => {
        let list = guias || [];

        if (filtroVencimento === "VENCIDOS") {
            list = list.filter((g: any) => isVencida(g?.vencimento));
        } else if (filtroVencimento === "NAO_VENCIDOS") {
            list = list.filter((g: any) => !isVencida(g?.vencimento));
        }

        const t = normalize(termo);
        if (!t) return list;

        return list.filter((g: any) => {
            const haystack = normalize(
                [g?.descr1, g?.descr2, g?.descr3, g?.descricao, g?.unico, g?.registro, g?.parcela]
                    .filter(Boolean)
                    .join(" ")
            );
            return haystack.includes(t);
        });
    }, [guias, termo, filtroVencimento]);

    const getQuantidadeParcelasExibidas = (rows: ParcelaResumo[]) => {
        const parcelas = rows
            .map((r: any) => Number(r?.parcela))
            .filter((n) => Number.isFinite(n));

        const parcelasMaioresQueZero = parcelas.filter((p) => p > 0);

        if (parcelasMaioresQueZero.length > 0) {
            return parcelasMaioresQueZero.length;
        }

        const parcelasZero = parcelas.filter((p) => p === 0);
        return parcelasZero.length;
    };

    const getParcelasLabel = (rows: ParcelaResumo[]) => {
        const parcelas = rows
            .map((r: any) => Number(r?.parcela))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);

        if (parcelas.length === 0) return "-";

        const parcelasMaioresQueZero = parcelas.filter((p) => p > 0);

        if (parcelasMaioresQueZero.length > 0) {
            return parcelasMaioresQueZero.join(", ");
        }

        return parcelas.filter((p) => p === 0).join(", ");
    };

    const getVencimentosLabel = (rows: ParcelaResumo[]) => {
        const vencimentos = rows
            .map((r: any) => r?.vencimento)
            .filter(Boolean)
            .sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());

        if (vencimentos.length === 0) return "-";

        return vencimentos.map((v: any) => formatDate(v)).join(", ");
    };


    const getValorTotalCarne = (rows: ParcelaResumo[]) => {
        const parcelasValidas = rows.map((r: any) => ({
            parcela: Number(r?.parcela),
            valor: Number(r?.valor ?? 0),
        }));

        const parcelasMaioresQueZero = parcelasValidas.filter(
            (p) => Number.isFinite(p.parcela) && p.parcela > 0
        );

        if (parcelasMaioresQueZero.length > 0) {
            return parcelasMaioresQueZero.reduce((acc, p) => {
                return acc + (Number.isFinite(p.valor) ? p.valor : 0);
            }, 0);
        }

        const parcelasZero = parcelasValidas.filter(
            (p) => Number.isFinite(p.parcela) && p.parcela === 0
        );

        if (parcelasZero.length > 0) {
            return parcelasZero.reduce((acc, p) => {
                return acc + (Number.isFinite(p.valor) ? p.valor : 0);
            }, 0);
        }

        return parcelasValidas.reduce((acc, p) => {
            return acc + (Number.isFinite(p.valor) ? p.valor : 0);
        }, 0);
    };

    const carnesAgrupados = useMemo(() => {
        const mapa = new Map<string, CarneAgrupado>();

        (guiasFiltradas || []).forEach((g: any) => {
            const chave = String(g?.registro ?? "").trim() || `sem-registro-${g?.parcela}-${g?.vencimento}`;

            if (!mapa.has(chave)) {
                mapa.set(chave, {
                    registro: chave,
                    rows: [],
                    descr1: g?.descr1,
                    descr2: g?.descr2,
                    descr3: g?.descr3,
                    total: 0,
                    parcelas: 0,
                    vencidas: 0,
                    proximoVencimento: undefined,
                    ultimoVencimento: undefined,
                });
            }

            const item = mapa.get(chave)!;
            item.rows.push(g);

            item.parcelas += 1;

            if (isVencida(g?.vencimento)) {
                item.vencidas += 1;
            }

            const venc = g?.vencimento ? new Date(g.vencimento) : null;

            if (venc && !isNaN(venc.getTime())) {
                if (
                    !item.proximoVencimento ||
                    new Date(item.proximoVencimento).getTime() > venc.getTime()
                ) {
                    item.proximoVencimento = g.vencimento;
                }

                if (
                    !item.ultimoVencimento ||
                    new Date(item.ultimoVencimento).getTime() < venc.getTime()
                ) {
                    item.ultimoVencimento = g.vencimento;
                }
            }
        });

        return Array.from(mapa.values())
            .map((item) => ({
                ...item,
                total: getValorTotalCarne(item.rows),
            }))
            .sort((a, b) => {
                const da = a.proximoVencimento ? new Date(a.proximoVencimento).getTime() : 0;
                const db = b.proximoVencimento ? new Date(b.proximoVencimento).getTime() : 0;
                return da - db;
            });
    }, [guiasFiltradas]);

    const totalCarnes = carnesAgrupados.length;


    const valorTotal = useMemo(() => {
        return (carnesAgrupados || []).reduce((acc: number, carne) => {
            const v = Number(carne?.total ?? 0);
            return acc + (Number.isFinite(v) ? v : 0);
        }, 0);
    }, [carnesAgrupados]);

    const totalVencidos = useMemo(
        () => (guiasFiltradas || []).filter((g: any) => isVencida(g?.vencimento)).length,
        [guiasFiltradas]
    );

    useEffect(() => {
        if (!banco) return;
        getEntidadePorNomeDoBanco(banco);
    }, [banco]);

    useEffect(() => {
        if (!pesquisado) setModalBuscaOpen(true);
    }, [pesquisado]);

    const cpfCnpjValido = useMemo(() => {
        const len = (cpfCnpjDraft || "").replace(/\D/g, "").length;
        return len === 11 || len === 14;
    }, [cpfCnpjDraft]);

    const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 14);
        setCpfCnpjDraft(onlyDigits);
    };

    const handleBuscar = () => {
        if (!id_entidade) return;

        const onlyDigits = (cpfCnpjDraft || "").replace(/\D/g, "");
        if (onlyDigits.length !== 11 && onlyDigits.length !== 14) return;

        setCpfCnpj(onlyDigits);
        setPesquisado(onlyDigits);

        getGuiasPorCpfCnpj(onlyDigits, entidade.banco);

        setModalBuscaOpen(false);
    };

    const handleLimpar = () => {
        setCpfCnpj("");
        setCpfCnpjDraft("");
        setPesquisado("");
        setTermo("");
        setFiltroVencimento("TODOS");
        clear();
        setModalBuscaOpen(true);
    };

    const abrirCarne = (rows: ParcelaResumo[]) => {
        setCobrancaFocus(rows);
        setOpenCarneDialog(true);
    };



    return (
        <Box sx={{ p: { xs: 1.25, md: 2 } }}>
            <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
                {/* HEADER */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {entidade?.logo && (
                            <Box
                                sx={{
                                    width: { xs: 44, md: 64 },
                                    height: { xs: 44, md: 64 },
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#fff",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src={entidade.logo}
                                    alt="Logo entidade"
                                    style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
                                />
                            </Box>
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", textTransform: "uppercase" }}>
                                {entidade?.titulo || " "}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: 18, md: 24 },
                                    fontWeight: 900,
                                    letterSpacing: 0.3,
                                    color: theme.colors.texto,
                                    lineHeight: 1.15,
                                }}
                            >
                                Guias de Arrecadação
                            </Typography>

                            <Typography sx={{ mt: 0.3, fontSize: 13, color: "text.secondary", fontWeight: 500 }}>
                                {id_entidade
                                    ? "Consulte suas guias em aberto informando o CPF ou CNPJ do responsável."
                                    : "Carregando informações da entidade..."}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {!!pesquisado && (
                            <Chip
                                label={`Pesquisado: ${maskCpfCnpj(pesquisado)}`}
                                sx={{ fontWeight: 900, borderRadius: 999, background: "rgba(25, 118, 210, 0.08)" }}
                            />
                        )}

                        <Button
                            fullWidth={isMobile}
                            size="small"
                            variant="outlined"
                            onClick={handleLimpar}
                            startIcon={<ClearRoundedIcon />}
                            disabled={!pesquisado && guias.length === 0}
                            sx={{ fontWeight: 900, borderRadius: 2 }}
                        >
                            Limpar
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* FILTROS */}
                {!!pesquisado && guias.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "rgba(0,0,0,0.02)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <TextField
                            fullWidth
                            value={termo}
                            onChange={(e) => setTermo(e.target.value)}
                            placeholder="Filtrar por descrição (ex: água, IPTU, taxa...)"
                            label="Filtrar guias"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: termo ? (
                                    <InputAdornment position="end">
                                        <Button onClick={() => setTermo("")} size="small" sx={{ fontWeight: 900 }}>
                                            Limpar
                                        </Button>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                            }}
                        />

                        <ToggleButtonGroup
                            exclusive
                            value={filtroVencimento}
                            onChange={(_, v) => v && setFiltroVencimento(v)}
                            sx={{
                                width: "100%",
                                display: "flex",
                                gap: 1,
                                "& .MuiToggleButtonGroup-grouped": {
                                    flex: 1,
                                    borderRadius: 2,
                                    border: "1px solid rgba(0,0,0,0.12) !important",
                                    fontWeight: 900,
                                    textTransform: "none",
                                    py: 1,
                                },
                                "& .Mui-selected": {
                                    background: "rgba(25,118,210,0.16) !important",
                                    color: "rgb(25,118,210) !important",
                                },
                            }}
                        >
                            <ToggleButton value="TODOS">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", width: "100%" }}>
                                    <AllInclusiveRoundedIcon fontSize="small" />
                                    Todos
                                </Box>
                            </ToggleButton>

                            <ToggleButton value="VENCIDOS">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", width: "100%" }}>
                                    <EventBusyRoundedIcon fontSize="small" />
                                    Vencidos
                                </Box>
                            </ToggleButton>

                            <ToggleButton value="NAO_VENCIDOS">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", width: "100%" }}>
                                    <EventAvailableRoundedIcon fontSize="small" />
                                    Não vencidos
                                </Box>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* LISTAGEM: desktop tabela / mobile cards */}
                {guias.length === 0 ? (
                    <Typography align="left" variant="body2" color="text.secondary">
                        {!id_entidade
                            ? "Carregando entidade..."
                            : !pesquisado
                                ? "Informe um CPF ou CNPJ para listar as guias."
                                : "Nenhuma guia encontrada para o CPF/CNPJ informado."}
                    </Typography>
                ) : carnesAgrupados.length === 0 ? (
                    <Typography align="left" variant="body2" color="text.secondary">
                        Nenhuma guia corresponde ao filtro aplicado.
                    </Typography>
                ) : isMobile ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {carnesAgrupados.map((carne) => {
                            const temVencida = carne.vencidas > 0;

                            return (
                                <Box
                                    key={carne.registro}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid rgba(0,0,0,0.08)",
                                        background: "#fff",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.06)",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                                        {carne.descr1 && (
                                            <Typography sx={{ fontSize: 15, fontWeight: 900, color: theme.colors.texto, lineHeight: 1.25 }}>
                                                {carne.descr1}
                                            </Typography>
                                        )}
                                        {carne.descr2 && (
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "rgba(0,0,0,0.75)", lineHeight: 1.25 }}>
                                                {carne.descr2}
                                            </Typography>
                                        )}
                                        {carne.descr3 && (
                                            <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25 }}>
                                                {carne.descr3}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                        <Chip
                                            label={`${getQuantidadeParcelasExibidas(carne.rows)} parcela${getQuantidadeParcelasExibidas(carne.rows) === 1 ? "" : "s"}`} sx={{
                                                fontWeight: 900,
                                                borderRadius: 999,
                                                background: "rgba(25, 118, 210, 0.10)",
                                                color: "rgb(25, 118, 210)",
                                            }}
                                        />

                                        <Chip
                                            label={
                                                carne.vencidas > 0
                                                    ? `${carne.vencidas} vencida${carne.vencidas === 1 ? "" : "s"}`
                                                    : "Em dia"
                                            }
                                            sx={{
                                                fontWeight: 900,
                                                borderRadius: 999,
                                                background: temVencida ? "rgba(211,47,47,0.12)" : "rgba(46,125,50,0.12)",
                                                color: temVencida ? "rgb(211,47,47)" : "rgb(46,125,50)",
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>
                                            Vencimento inicial: {formatDate(carne.proximoVencimento)}
                                        </Typography>

                                        {carne.parcelas > 1 && (
                                            <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>
                                                Vencimento final: {formatDate(carne.ultimoVencimento)}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>
                                            Valor total
                                        </Typography>
                                        <Typography sx={{ fontSize: 18, fontWeight: 900, color: theme.colors.texto }}>
                                            {formatMoney(carne.total)}
                                        </Typography>
                                    </Box>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => abrirCarne(carne.rows)}
                                        sx={{ borderRadius: 2, fontWeight: 900 }}
                                    >
                                        Visualizar carnê
                                    </Button>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        "& th": {
                                            fontWeight: 900,
                                            color: theme.colors.texto,
                                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                                        },
                                    }}
                                >
                                    <TableCell align="left">Carnê</TableCell>
                                    <TableCell align="left" sx={{ width: 280 }}>Vencimentos</TableCell>
                                    <TableCell align="left" sx={{ width: 220 }}>Parcelas</TableCell>
                                    <TableCell align="right" sx={{ width: 170 }}>Valor total</TableCell>
                                    <TableCell align="center" sx={{ width: 140 }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {carnesAgrupados.map((carne) => {
                                    const temVencida = carne.vencidas > 0;

                                    return (
                                        <TableRow
                                            key={carne.registro}
                                            hover
                                            sx={{ "& td": { borderBottomColor: "rgba(0,0,0,0.06)" } }}
                                        >
                                            <TableCell align="left" sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                                    {carne.descr1 && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 15,
                                                                fontWeight: 900,
                                                                color: theme.colors.texto,
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {carne.descr1}
                                                        </Typography>
                                                    )}

                                                    {carne.descr2 && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 13.5,
                                                                fontWeight: 600,
                                                                color: "rgba(0,0,0,0.75)",
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {carne.descr2}
                                                        </Typography>
                                                    )}

                                                    {carne.descr3 && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12.5,
                                                                color: "text.secondary",
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {carne.descr3}
                                                        </Typography>
                                                    )}

                                                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                                                        Registro: {carne.registro}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell align="left">
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: temVencida ? "rgb(211,47,47)" : "rgb(46,125,50)",
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {getVencimentosLabel(carne.rows)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="left">
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: "rgb(25, 118, 210)",
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {getParcelasLabel(carne.rows)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography
                                                    sx={{
                                                        fontSize: 16,
                                                        fontWeight: 900,
                                                        color: theme.colors.texto,
                                                    }}
                                                >
                                                    {formatMoney(carne.total)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Button
                                                    onClick={() => abrirCarne(carne.rows)}
                                                    size="small"
                                                    variant="contained"
                                                    sx={{ borderRadius: 2, fontWeight: 900 }}
                                                >
                                                    Visualizar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* RODAPÉ */}
                {guiasFiltradas.length > 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "rgba(0,0,0,0.02)",
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { xs: "stretch", md: "center" },
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                                label={`${totalCarnes} carnê${totalCarnes === 1 ? "" : "s"}`}
                                sx={{ fontWeight: 900, borderRadius: 999, background: "rgba(25, 118, 210, 0.10)", color: "rgb(25, 118, 210)" }}
                            />

                            {totalVencidos > 0 && (
                                <Chip
                                    label={`${totalVencidos} vencida${totalVencidos === 1 ? "" : "s"}`}
                                    sx={{ fontWeight: 900, borderRadius: 999, background: "rgba(211,47,47,0.12)", color: "rgb(211,47,47)" }}
                                />
                            )}

                            <Chip
                                label={`Total: ${formatMoney(valorTotal)}`}
                                sx={{ fontWeight: 900, borderRadius: 999, background: "rgba(46, 125, 50, 0.12)", color: "rgb(46, 125, 50)" }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", md: "row" } }}>
                            <Button
                                fullWidth={isMobile}
                                variant="outlined"
                                onClick={handleLimpar}
                                startIcon={<ClearRoundedIcon />}
                                sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                                Limpar
                            </Button>

                            {/* <Button
                                fullWidth={isMobile}
                                variant="contained"
                                onClick={() => abrirCarne(guiasFiltradas as ParcelaResumo[])}
                                sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                                Visualizar todos
                            </Button> */}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* MODAL DE BUSCA */}
            <Dialog
                open={modalBuscaOpen}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: { xs: 1, md: 2 },
                        minHeight: { xs: "auto", md: 320 },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 900,
                        color: theme.colors.texto,
                        fontSize: { xs: 18, md: 26 },
                        pb: 1,
                    }}
                >
                    Buscar guias
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography sx={{ fontSize: { xs: 13, md: 16 }, color: "text.secondary", mb: 3, lineHeight: 1.5 }}>
                        Digite o <b>CPF (11)</b> ou <b>CNPJ (14)</b> do pagador para listar as guias em aberto.
                    </Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        size="medium"
                        label="CPF/CNPJ"
                        value={cpfCnpjDraft}
                        onChange={handleDraftChange}
                        placeholder="Ex.: 12345678901"
                        inputProps={{ inputMode: "numeric", pattern: "\\d*", maxLength: 14 }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleBuscar();
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiInputLabel-root": { fontSize: { xs: 14, md: 18 } },
                            "& .MuiOutlinedInput-input": {
                                fontSize: { xs: 16, md: 22 },
                                py: { xs: 1.2, md: 1.6 },
                            },
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                            "& .MuiFormHelperText-root": { fontSize: { xs: 12, md: 14 } },
                        }}
                        helperText={
                            !id_entidade
                                ? "Carregando entidade..."
                                : cpfCnpjDraft.length === 0
                                    ? "Digite somente números."
                                    : cpfCnpjValido
                                        ? "Perfeito. Pressione Enter ou clique em Buscar."
                                        : "CPF deve ter 11 dígitos ou CNPJ 14 dígitos."
                        }
                    />
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 2 }}>
                    <Button
                        variant="text"
                        onClick={handleLimpar}
                        startIcon={<ClearRoundedIcon />}
                        sx={{ fontWeight: 900, fontSize: { xs: 13, md: 16 } }}
                    >
                        Limpar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleBuscar}
                        startIcon={<SearchRoundedIcon />}
                        disabled={!id_entidade || !cpfCnpjValido}
                        sx={{
                            fontWeight: 900,
                            borderRadius: 2,
                            px: { xs: 2, md: 3 },
                            py: { xs: 1, md: 1.2 },
                            fontSize: { xs: 13, md: 16 },
                        }}
                    >
                        Buscar
                    </Button>
                </DialogActions>
            </Dialog>

            {openCarneDialog && cobrancaFocus.length > 0 && (
                <CarneDialog
                    onClose={() => {
                        setCobrancaFocus([]);
                        setOpenCarneDialog(false);
                    }}
                    open
                    guias={cobrancaFocus}
                />
            )}
        </Box>
    );
};
